import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { When } from 'react-if';
import { Tooltip } from '@mui/material';
import { useLocalStorage } from 'usehooks-ts';
import {
  ArrowPathIcon,
  CheckIcon,
  ChevronDownIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  PhoneIcon,
} from '@heroicons/react/20/solid';
import {
  ActionStatus,
  IEscalationResponseItem,
  INote,
  InvoiceEventType,
  langauge,
} from 'shared';

import { classNames } from '../../../../common/lib/classNames';
import { DatePopover } from '../../../../common/Components/DatePopover';
import { PillBadge } from '../../../../common/Atoms/PillBadge';
import { useAccountingSystemName } from '../../../hooks/useAccountingSystemName';
import { useActions } from '../../../hooks/useActions';
import { useEscalations } from '../../../hooks/useEscalations';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';
import { useInvoice } from '../../../hooks/useInvoice';
import { useIsSafeMode } from '../../../hooks/useIsSafeMode';
import { useRecalculateActionsMutation } from '../../../services/api/actionApi/action';
import { useCreateNoteMutation } from '../../../services/api/invoiceApi/invoice';
import { LogActivityModal } from '../../CRM/ContactPage/LogActivityModal';
import {
  ActivityFeedTimeline,
  IFeedItem,
  noteItem,
  SegmentedControl,
} from '../../Molecules/ActivityFeedTimeline';
import { NoteComposer } from '../../Molecules/Notes';
import { Message as ActionMessage } from '../../TimeLine/TimelineBasicItem';
import { WrappedLink } from '../../WrappedLink';
import { buildInvoiceEventRows } from '../InvoiceDocument/InvoiceEvents';

type FeedKind = `note` | `event` | `action`;
type FeedFilter = `all` | FeedKind;

interface FeedItem extends IFeedItem {
  kind: FeedKind;
}

interface PendingNote {
  id: number;
  content: string;
  createdAt: Date;
  /** Number of notes on the invoice when this was submitted, hidden once the real note arrives */
  notesBefore: number;
}

const filters: { id: FeedFilter; label: string }[] = [
  { id: `all`, label: `All` },
  { id: `note`, label: `Notes` },
  { id: `event`, label: `Events` },
  { id: `action`, label: `Actions` },
];

function EscalationCard({ escalation, invoiceId }: { escalation: IEscalationResponseItem; invoiceId: string }) {
  const organisation = useGetSelectedOrganisation();
  const [collapsed, setCollapsed] = useLocalStorage(`invoiceEscalationCollapsed:${invoiceId}:${escalation.id}`, false);

  return (
    <div className={ `flex flex-col rounded-lg border border-red-200 bg-[#fffafa]` }>
      <button
        type={ `button` }
        onClick={ () => setCollapsed(!collapsed) }
        aria-expanded={ !collapsed }
        className={ `cursor-pointer flex items-center justify-between gap-3 px-3.5 py-2.5 text-left` }
      >
        <span className={ `flex shrink-0 items-center gap-2 whitespace-nowrap text-sm font-semibold text-gray-900` }>
          <PhoneIcon className={ `h-3.5 w-3.5 text-red-700` } />
          { langauge.escalation.type[escalation.escalationType]?.label || escalation.escalationType }
        </span>
        <span className={ `flex items-center gap-2.5` }>
          <PillBadge
            color={ `orange` }
            bordered
            className={ `!py-px` }
          >
            { `Open escalation` }
          </PillBadge>
          <ChevronDownIcon className={ classNames(`h-4 w-4 text-gray-500 transition-transform`, !collapsed && `rotate-180`) } />
        </span>
      </button>
      <When condition={ !collapsed }>
        <div className={ `flex items-end justify-between gap-4 px-3.5 pb-3` }>
          <div className={ `text-[0.8125rem] leading-normal text-gray-600` }>
            { `Scheduled ` }
            <span className={ `text-gray-900` }>
              <DatePopover
                disablePopover
                date={ escalation.escalationDate }
                labelFormat={ `ccc d LLL yyyy` }
                organisationTimezone={ organisation?.validatedTimezone }
              />
            </span>
            { ` · Assigned to ` }
            <span className={ `text-gray-900` }>
              { escalation.assignedUser ? `${escalation.assignedUser.firstName} ${escalation.assignedUser.lastName}` : `Unassigned` }
            </span>
            { ` · Policy ` }
            <span className={ `text-gray-900` }>
              { escalation.policyId ? escalation.policy?.title || `-` : `Manual` }
            </span>
          </div>
          <WrappedLink
            to={ `/escalations/${escalation.id}/contact/${escalation.contactId}` }
            className={ `whitespace-nowrap text-[0.8125rem] text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline` }
          >
            { `View escalation →` }
          </WrappedLink>
        </div>
      </When>
    </div>
  );
}

/** Notes, lifecycle events and Paidnice actions for the invoice in one thread, oldest first */
export function InvoiceActivityFeed() {
  const { invoiceId } = useParams();
  const organisation = useGetSelectedOrganisation();
  const { invoice, data: invoiceData } = useInvoice();
  const { data: actionsData } = useActions();
  const { data: escalationData } = useEscalations();
  const [recalculateActions, { isLoading: recalculating }] = useRecalculateActionsMutation();
  const [postNewNote, { isLoading: postingNote }] = useCreateNoteMutation();
  const accountingName = useAccountingSystemName();
  const safemode = useIsSafeMode();
  const [filter, setFilter] = useState<FeedFilter>(`all`);
  const [pendingNotes, setPendingNotes] = useState<PendingNote[]>([]);
  // Bumped when the user adds something, so the thread scrolls down to it
  const [follows, setFollows] = useState(0);
  const [recordActivityOpen, setRecordActivityOpen] = useState(false);

  const timezone = organisation?.validatedTimezone;
  const notes: INote[] = useMemo(() => invoiceData?.notes || [], [invoiceData?.notes]);

  const items = useMemo(() => {
    const now = new Date();
    const all: FeedItem[] = [];

    buildInvoiceEventRows(invoice).forEach(row => {
      const [firstLine, ...rest] = row.content.split(`\n`);
      // "Line items changed:" reads as a heading once its detail sits underneath
      const title = rest.length ? firstLine.replace(/:\s*$/, ``) : firstLine;

      all.push({
        key: `event-${row.id}`,
        kind: `event`,
        at: row.createdAt,
        icon: row.icon,
        iconClass: row.iconBackground,
        title,
        detail: rest.join(`\n`) || null,
        collapsible: row.type === InvoiceEventType.LINE_ITEMS_CHANGED && rest.length > 0,
      });
    });

    notes.forEach(note => all.push({ ...noteItem(note, accountingName), kind: `note` }));

    pendingNotes
      .filter(pending => notes.length <= pending.notesBefore)
      .forEach(pending => all.push({
        key: `pending-${pending.id}`,
        kind: `note`,
        at: pending.createdAt,
        icon: PencilIcon,
        iconClass: `bg-paidniceGreen-600`,
        title: `You added a note`,
        body: pending.content,
        timeLabel: `Sending...`,
        pending: true,
      }));

    (actionsData?.actions || []).forEach(action => {
      const scheduled = [ActionStatus.PENDING, ActionStatus.APPROVAL_PENDING].includes(action.status);
      const dueNow = action.status === ActionStatus.PENDING && new Date(action.actionOn) < now && !safemode;

      all.push({
        key: `action-${action.id}`,
        kind: `action`,
        at: dueNow ? now : new Date(action.executedOn || action.actionOn),
        icon: action.status === ActionStatus.COMPLETE ? CheckIcon
          : action.status === ActionStatus.FAILED ? ExclamationTriangleIcon
            : ClockIcon,
        iconClass: action.status === ActionStatus.COMPLETE ? `bg-green-500`
          : action.status === ActionStatus.FAILED ? `bg-red-500`
            : `bg-gray-400`,
        title: <ActionMessage action={ action } />,
        timeLabel: dueNow ? `Within 5 minutes` : undefined,
        pending: scheduled,
      });
    });

    return all.sort((a, b) => a.at.getTime() - b.at.getTime());
  }, [invoice, notes, pendingNotes, actionsData, accountingName, safemode]);

  const visibleItems = useMemo(() => items.filter(item => filter === `all` || item.kind === filter), [items, filter]);

  async function onSaveNote(content: string, mentionedUsers: string[], attachments: File[], syncAttachments: boolean) {
    const pending: PendingNote = { id: Date.now(), content, createdAt: new Date(), notesBefore: notes.length };

    setPendingNotes(current => [...current, pending]);
    setFollows(n => n + 1);

    const result = await postNewNote({
      organisationId: invoice.organisation_id,
      invoiceId: invoice.id,
      content,
      mentionedUsers,
      attachments,
      syncAttachments,
    });

    if (`error` in result) {
      setPendingNotes(current => current.filter(p => p.id !== pending.id));
    }
  }

  if (!invoice) return null;

  const escalations = escalationData?.escalations || [];

  return (
    <div className={ `flex h-[40rem] flex-col rounded-xl border border-gray-200 bg-white xl:h-auto xl:min-h-0 xl:flex-1` }>
      <div className={ `flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3` }>
        <span className={ `flex items-center gap-2` }>
          <span className={ `text-sm font-semibold text-gray-900` }>{ `Activity` }</span>
          <When condition={ filter === `all` || filter === `action` }>
            <Tooltip title={ `Runs automatically several times daily` }>
              <button
                type={ `button` }
                onClick={ () => recalculateActions({ organisationId: invoice.organisation_id, invoiceId: invoice.id }) }
                disabled={ recalculating }
                className={ `group cursor-pointer flex h-[1.625rem] items-center rounded-md border border-gray-200 px-[0.3125rem] text-paidniceGreen-600 hover:bg-paidniceGreen-50 disabled:cursor-not-allowed` }
              >
                <ArrowPathIcon className={ classNames(`h-3.5 w-3.5 shrink-0`, recalculating && `animate-spin`) } />
                <span className={ `max-w-0 overflow-hidden whitespace-nowrap text-xs font-medium transition-all duration-200 group-hover:ml-1.5 group-hover:max-w-[10rem] group-focus-visible:ml-1.5 group-focus-visible:max-w-[10rem]` }>
                  { `Recalculate Actions` }
                </span>
              </button>
            </Tooltip>
          </When>
        </span>
        <SegmentedControl
          filters={ filters }
          value={ filter }
          onChange={ setFilter }
        />
      </div>

      <When condition={ escalations.length > 0 }>
        <div className={ `mx-4 mt-3 flex shrink-0 flex-col gap-2` }>
          { escalations.map(escalation => (
            <EscalationCard
              key={ escalation.id }
              escalation={ escalation }
              invoiceId={ invoiceId }
            />
          )) }
        </div>
      </When>

      <ActivityFeedTimeline
        items={ visibleItems }
        timezone={ timezone }
        followKey={ `${filter}:${follows}` }
        footer={ filter === `event` ? (
          <p className={ `pt-2 text-xs text-gray-500` }>
            { `Only a few key invoice lifecycle events are recorded here. Refer to actions for things like late fees & emails.` }
          </p>
        ) : null }
      />

      <div className={ `shrink-0 border-t border-gray-100 px-3 py-2.5` }>
        <NoteComposer
          compact
          onSave={ onSaveNote }
          loading={ postingNote }
        />
        <button
          type={ `button` }
          onClick={ () => setRecordActivityOpen(true) }
          className={ `cursor-pointer mt-1.5 text-xs text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline` }
        >
          { `Record activity` }
        </button>
      </div>

      <LogActivityModal
        open={ recordActivityOpen }
        setOpen={ setRecordActivityOpen }
        organisationId={ invoice.organisation_id }
        contactId={ invoice.contact_id }
        invoiceId={ invoice.id }
      />
    </div>
  );
}
